import { API_BASE_URL } from '../config';
import {
  NginxDashboardSummary,
  NginxSiteDetail,
  NginxAdsComparison,
  NginxTopVisitors,
  HourlyData,
  PageData,
  NginxTrafficSources,
  NginxCollectionRun,
  NginxCollectNowResponse,
  BreakdownItem,
  StatusBreakdownItem,
  UtmBreakdownItem,
  CountryBreakdownItem,
  SlowRequest,
  ClientTypeItem,
  RequestCategoryItem,
  SslProtocolItem,
} from '../types';

const NGINX_BASE = `${API_BASE_URL}/analytics/nginx`;
const NGINX_ADMIN = `${NGINX_BASE}/admin`;

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

async function fetchJson<T>(url: string, accessToken: string, method = 'GET'): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.message || payload?.error || `Erro HTTP ${response.status}`);
  }

  const payload = await response.json();
  // Backend ResponseWrapperFilter wraps all responses in {success, data}
  return payload.data !== undefined ? payload.data : payload;
}

/**
 * Fetch dashboard summary with all sites for a given date or date range
 */
export async function getDashboard(
  accessToken: string,
  dateFrom?: string,
  dateTo?: string
): Promise<NginxDashboardSummary> {
  return fetchJson(`${NGINX_BASE}/dashboard${buildQuery({ dateFrom, dateTo })}`, accessToken);
}

/**
 * Extract array items from JSONB wrapper objects.
 * Backend stores JSONB as {items: [...]} or {hours: [...]} wrappers.
 */
function extractItems<T>(obj: unknown, key = 'items'): T[] {
  if (!obj || typeof obj !== 'object') return [];
  if (Array.isArray(obj)) return obj as T[];
  const record = obj as Record<string, unknown>;
  if (Array.isArray(record[key])) return record[key] as T[];
  return [];
}

/** Normalize a JSONB breakdown item that uses a named field (channel, type, browser, etc.) into {name, count, visitors, percentage} */
function normalizeBreakdownItems(obj: unknown, nameField: string): BreakdownItem[] {
  return extractItems<Record<string, unknown>>(obj).map((item) => ({
    name: String(item[nameField] || item['name'] || ''),
    count: Number(item['count'] || 0),
    visitors: Number(item['visitors'] || 0),
    percentage: Number(item['percentage'] || 0),
  }));
}

/**
 * Fetch detailed analytics for a specific site
 */
export async function getSiteDetail(
  accessToken: string,
  siteId: number,
  dateFrom?: string,
  dateTo?: string
): Promise<NginxSiteDetail> {
  const raw = await fetchJson<Record<string, unknown>>(`${NGINX_BASE}/sites/${siteId}${buildQuery({ dateFrom, dateTo })}`, accessToken);

  // Transform JSONB wrapper objects into flat arrays the UI expects
  const statusItems = extractItems<Record<string, unknown>>(raw['statusBreakdown']);
  const statusTotal = statusItems.reduce((sum, i) => sum + Number(i['count'] || 0), 0);

  const rawResponseTimeStats = raw['responseTimeStats'];
  let responseTimeStats: NginxSiteDetail['responseTimeStats'] = null;
  if (rawResponseTimeStats && typeof rawResponseTimeStats === 'object') {
    const statsObj = !Array.isArray(rawResponseTimeStats)
      ? (rawResponseTimeStats as Record<string, unknown>)
      : ((rawResponseTimeStats as unknown[])[0] as Record<string, unknown> | undefined) ?? {};
    const slowReqs = extractItems<Record<string, unknown>>(statsObj['slowRequests'] ?? []);
    responseTimeStats = {
      avg: Number(statsObj['avg'] || 0),
      p50: Number(statsObj['p50'] || 0),
      p95: Number(statsObj['p95'] || 0),
      p99: Number(statsObj['p99'] || 0),
      max: Number(statsObj['max'] || 0),
      slowRequests: slowReqs.map((sr): SlowRequest => ({
        path: String(sr['path'] || ''),
        avgTime: Number(sr['avgTime'] || 0),
        count: Number(sr['count'] || 0),
      })),
    };
  }

  const rawFunnelData = raw['funnelData'];
  const funnelData =
    rawFunnelData && typeof rawFunnelData === 'object' && !Array.isArray(rawFunnelData)
      ? ((rawFunnelData as Record<string, unknown>)['steps'] ?? null)
      : (rawFunnelData ?? null);

  return {
    ...(raw as Omit<NginxSiteDetail, keyof NginxSiteDetail>),
    siteId: raw['siteId'] as number,
    name: raw['name'] as string,
    slug: raw['slug'] as string,
    color: raw['color'] as string,
    domain: raw['domain'] as string,
    date: raw['date'] as string,
    totalRequests: raw['totalRequests'] as number,
    uniqueVisitors: raw['uniqueVisitors'] as number,
    totalPageViews: raw['totalPageViews'] as number,
    totalBytes: raw['totalBytes'] as number,
    botRequests: raw['botRequests'] as number,
    avgRequestsPerVisitor: raw['avgRequestsPerVisitor'] as number,
    hourlyBreakdown: extractItems<HourlyData>(raw['hourlyBreakdown'], 'hours'),
    deviceBreakdown: normalizeBreakdownItems(raw['deviceBreakdown'], 'type'),
    browserBreakdown: normalizeBreakdownItems(raw['browserBreakdown'], 'browser'),
    osBreakdown: normalizeBreakdownItems(raw['osBreakdown'], 'os'),
    statusBreakdown: statusItems.map((item): StatusBreakdownItem => ({
      status: Number(item['status'] || 0),
      count: Number(item['count'] || 0),
      percentage: statusTotal > 0 ? Math.round((Number(item['count'] || 0) / statusTotal) * 1000) / 10 : 0,
    })),
    trafficChannelBreakdown: normalizeBreakdownItems(raw['trafficChannelBreakdown'], 'channel'),
    utmBreakdown: extractItems<Record<string, unknown>>(raw['utmBreakdown']).map((item): UtmBreakdownItem => ({
      source: String(item['source'] || '(not set)'),
      medium: String(item['medium'] || '(not set)'),
      campaign: String(item['campaign'] || '(not set)'),
      count: Number(item['count'] || 0),
      visitors: Number(item['visitors'] || 0),
    })),
    refererBreakdown: extractItems<Record<string, unknown>>(raw['refererBreakdown']).map((item): BreakdownItem => ({
      name: String(item['domain'] || item['name'] || ''),
      count: Number(item['count'] || 0),
      visitors: Number(item['visitors'] || 0),
      percentage: Number(item['percentage'] || 0),
    })),
    topPages: extractItems<PageData>(raw['topPages']),
    topApiEndpoints: extractItems<PageData>(raw['topApiEndpoints']),
    funnelData: funnelData as NginxSiteDetail['funnelData'],
    adsData: (raw['adsData'] as NginxSiteDetail['adsData']) ?? null,
    // New breakdowns from enhanced nginx logging
    countryBreakdown: extractItems<Record<string, unknown>>(raw['countryBreakdown']).map((item): CountryBreakdownItem => ({
      country: String(item['country'] || ''),
      count: Number(item['count'] || 0),
      visitors: Number(item['visitors'] || 0),
      percentage: Number(item['percentage'] || 0),
    })),
    responseTimeStats,
    clientTypeBreakdown: extractItems<Record<string, unknown>>(raw['clientTypeBreakdown']).map((item): ClientTypeItem => ({
      clientType: String(item['clientType'] || ''),
      count: Number(item['count'] || 0),
      visitors: Number(item['visitors'] || 0),
      percentage: Number(item['percentage'] || 0),
    })),
    requestCategoryBreakdown: extractItems<Record<string, unknown>>(raw['requestCategoryBreakdown']).map((item): RequestCategoryItem => ({
      category: String(item['category'] || ''),
      count: Number(item['count'] || 0),
      percentage: Number(item['percentage'] || 0),
    })),
    sslProtocolBreakdown: extractItems<Record<string, unknown>>(raw['sslProtocolBreakdown']).map((item): SslProtocolItem => ({
      protocol: String(item['protocol'] || ''),
      count: Number(item['count'] || 0),
      percentage: Number(item['percentage'] || 0),
    })),
  } satisfies NginxSiteDetail;
}

/**
 * Fetch ads comparison data (paid vs organic)
 */
export async function getAdsComparison(
  accessToken: string,
  dateFrom?: string,
  dateTo?: string
): Promise<NginxAdsComparison> {
  return fetchJson(`${NGINX_BASE}/ads-vs-organic${buildQuery({ dateFrom, dateTo })}`, accessToken);
}

/**
 * Fetch top visitors for a specific site
 */
export async function getTopVisitors(
  accessToken: string,
  siteId: number,
  dateFrom?: string,
  dateTo?: string,
  limit?: number
): Promise<NginxTopVisitors> {
  return fetchJson(`${NGINX_BASE}/sites/${siteId}/visitors${buildQuery({ dateFrom, dateTo, limit })}`, accessToken);
}

/**
 * Fetch hourly traffic for a specific site
 */
export async function getHourlyTraffic(
  accessToken: string,
  siteId: number,
  dateFrom?: string,
  dateTo?: string
): Promise<HourlyData[]> {
  return fetchJson(`${NGINX_BASE}/sites/${siteId}/hourly${buildQuery({ dateFrom, dateTo })}`, accessToken);
}

/**
 * Fetch traffic sources breakdown
 */
export async function getTrafficSources(
  accessToken: string,
  dateFrom?: string,
  dateTo?: string
): Promise<NginxTrafficSources> {
  return fetchJson(`${NGINX_BASE}/traffic-sources${buildQuery({ dateFrom, dateTo })}`, accessToken);
}

/**
 * Fetch available dates with data
 */
export async function getAvailableDates(accessToken: string): Promise<string[]> {
  return fetchJson(`${NGINX_BASE}/dates`, accessToken);
}

/**
 * Fetch recent collection runs
 */
export async function getRecentCollections(
  accessToken: string,
  limit?: number
): Promise<NginxCollectionRun[]> {
  return fetchJson(`${NGINX_BASE}/collections${buildQuery({ limit })}`, accessToken);
}

/**
 * Trigger a new collection run
 */
export async function triggerCollection(
  accessToken: string
): Promise<NginxCollectNowResponse> {
  return fetchJson(`${NGINX_ADMIN}/collect`, accessToken, 'POST');
}

/**
 * Refresh snapshots for a specific date
 */
export async function refreshSnapshots(
  accessToken: string,
  date?: string
): Promise<void> {
  await fetchJson<unknown>(`${NGINX_ADMIN}/snapshots/refresh${buildQuery({ date })}`, accessToken, 'POST');
}

export const nginxAnalyticsService = {
  getDashboard,
  getSiteDetail,
  getAdsComparison,
  getTopVisitors,
  getHourlyTraffic,
  getTrafficSources,
  getAvailableDates,
  getRecentCollections,
  triggerCollection,
  refreshSnapshots,
};
